import type { Ref } from 'vue'
import { ref } from 'vue'
import { getAccountData } from '@app/application/report/api'
import type { AdjustmentsData, DetailedEntriesData, Top5Item } from '../types'
import {
  EMPTY_TABLE_DATA,
  camelizeTableData,
  prepareDetailedEntries,
} from '../utils/account-data-utils'

export default function useAccountData(): {
  ledgerLabel: Ref<string>
  ledgerId: Ref<number>
  accountTitle: Ref<string>
  accountSubtitle: Ref<string | null>
  accountNumber: Ref<string>
  accountLedgerUrl: Ref<string>
  top5: Ref<Array<Top5Item>>
  currencyNegativeFormat: Ref<string>
  entryPresent: Ref<boolean>
  canAddMessage: Ref<boolean>
  detailedEntries: Ref<DetailedEntriesData>
  adjustments: Ref<AdjustmentsData>
  fetchAccountData: (
    reportId: number,
    accountId: number,
    ledgerId: number,
    subtractLedgerId: number,
    relative: string,
    analyticalDimensionId?: number
  ) => Promise<void>
  refetch: () => Promise<void>
} {
  const ledgerLabel = ref('')
  const ledgerId = ref(0)
  const accountTitle: Ref<string> = ref('')
  const accountSubtitle: Ref<string | null> = ref(null)
  const accountNumber = ref('')
  const accountLedgerUrl = ref('')
  const top5: Ref<Array<Top5Item>> = ref([])
  const currencyNegativeFormat = ref('')
  const entryPresent = ref(false)
  const canAddMessage = ref(false)
  const detailedEntries: Ref<DetailedEntriesData> = ref({ ...EMPTY_TABLE_DATA })
  const adjustments: Ref<AdjustmentsData> = ref({ ...EMPTY_TABLE_DATA })

  let lastArgs: Parameters<typeof fetchAccountData> | null = null

  async function fetchAccountData(
    reportId: number,
    accountId: number,
    nextLedgerId: number,
    subtractLedgerId: number,
    relative: string,
    analyticalDimensionId?: number
  ): Promise<void> {
    lastArgs = [
      reportId,
      accountId,
      nextLedgerId,
      subtractLedgerId,
      relative,
      analyticalDimensionId,
    ]
    ledgerId.value = nextLedgerId

    const data = await getAccountData(
      reportId,
      accountId,
      nextLedgerId,
      subtractLedgerId,
      relative,
      analyticalDimensionId
    )

    if (data) {
      top5.value = data.top5
      currencyNegativeFormat.value = data.currencyNegativeFormat
      ledgerLabel.value = data.periodLabel
      accountTitle.value = data.accountTitle
      accountSubtitle.value = data.accountSubtitle
      accountNumber.value = data.accountNumber
      accountLedgerUrl.value = data.accountLedgerUrl
      entryPresent.value = data.entryPresent
      canAddMessage.value = data.canAddMessage
      detailedEntries.value = prepareDetailedEntries(
        data.detailedEntries || EMPTY_TABLE_DATA
      )
      adjustments.value = camelizeTableData(
        data.adjustments || EMPTY_TABLE_DATA
      )
    }
  }

  async function refetch(): Promise<void> {
    if (lastArgs) {
      await fetchAccountData(...lastArgs)
    }
  }

  return {
    ledgerLabel,
    ledgerId,
    accountTitle,
    accountSubtitle,
    accountNumber,
    accountLedgerUrl,
    top5,
    currencyNegativeFormat,
    entryPresent,
    canAddMessage,
    detailedEntries,
    adjustments,
    fetchAccountData,
    refetch,
  }
}
